import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { FileSpreadsheet, CheckCircle2 } from "lucide-react";

export default function PosFormats() {
    const [data, setData] = useState(null);

    useEffect(() => {
        http.get("/spykar/pos-formats").then((r) => setData(r.data));
    }, []);

    const list = data?.configured || [];

    return (
        <div className="mx-auto max-w-[1560px] space-y-4 p-6" data-testid="pos-formats-page">
            <div className="flex items-end justify-between">
                <div>
                    <div className="mlabel">Spykar Ops · POS Formats</div>
                    <h1 className="text-[22px] font-semibold tracking-tight">POS Format Catalog</h1>
                    <p className="mt-1 text-[12px] text-muted-foreground">
                        Retailer-specific POS layouts registered for ingestion. Target: 25 formats across LFS &amp; MBO tail.
                    </p>
                </div>
                {data && (
                    <div className="flex items-center gap-3 text-[11px]">
                        <Chip label={`${data.configured_count} Configured`} tone="green" />
                        <Chip label={`${data.remaining} Remaining`} tone="amber" />
                        <Chip label={`Target ${data.target_count}`} tone="muted" />
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {list.map((f) => (
                    <div key={f.id} className="rounded-sm border border-border bg-card p-4">
                        <div className="flex items-start justify-between">
                            <div className="flex items-start gap-2">
                                <div className="grid h-8 w-8 place-items-center rounded-sm bg-primary/10 text-primary">
                                    <FileSpreadsheet className="h-4 w-4" />
                                </div>
                                <div>
                                    <div className="text-[13px] font-semibold">{f.retailer}</div>
                                    <div className="mt-0.5 text-[11px] text-muted-foreground">
                                        {f.file_type.toUpperCase()} · sheet: {f.sheet} · key: {f.row_key}
                                    </div>
                                </div>
                            </div>
                            {f.active && (
                                <span className="inline-flex items-center gap-1 rounded-sm border border-emerald-500/30 bg-emerald-500/10 px-1.5 py-[1px] text-[10px] font-medium text-emerald-600">
                                    <CheckCircle2 className="h-3 w-3" /> Active
                                </span>
                            )}
                        </div>

                        <div className="mt-3">
                            <div className="mlabel mb-1">Columns ({f.columns.length})</div>
                            <div className="flex flex-wrap gap-1">
                                {f.columns.map((c) => (
                                    <span
                                        key={c}
                                        className="rounded-sm border border-border bg-muted/40 px-1.5 py-[1px] text-[10px] font-medium text-muted-foreground"
                                    >
                                        {c}
                                    </span>
                                ))}
                            </div>
                        </div>

                        <div className="mt-3">
                            <div className="mlabel mb-1">Validations</div>
                            <ul className="space-y-0.5 text-[11px] text-muted-foreground">
                                {f.validations.map((v, i) => (
                                    <li key={i} className="flex items-start gap-1.5">
                                        <span className="mt-1 h-1 w-1 shrink-0 rounded-full bg-primary" />
                                        {v}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                ))}
            </div>

            {data && data.remaining > 0 && (
                <div className="rounded-sm border border-dashed border-border bg-card p-6 text-center">
                    <div className="text-[13px] font-semibold">{data.remaining} POS formats pending onboarding</div>
                    <div className="mt-1 text-[11px] text-muted-foreground">
                        Register additional retailer POS formats to reach the 25-format target for Spykar MBO coverage.
                    </div>
                </div>
            )}
        </div>
    );
}

function Chip({ label, tone }) {
    const c =
        tone === "green"
            ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/30"
            : tone === "amber"
            ? "bg-amber-500/10 text-amber-600 border-amber-500/30"
            : "bg-muted text-muted-foreground border-border";
    return <span className={`rounded-sm border px-2 py-[2px] text-[10px] font-semibold ${c}`}>{label}</span>;
}
